import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { alpha } from '@mui/material/styles';

import CustomerCard from './SalesComponents/CustomerCard';
import LicenseUpload from './SalesComponents/LicenseUpload';
import InvoicePanel from './SalesComponents/InvoicePanel';
import DepositsPanel from './SalesComponents/DepositsPanel';
import ExpensesPanel from './SalesComponents/ExpensesPanel';
import TradesPanel from './SalesComponents/TradesPanel';
import SignedPacketSlot from './SalesComponents/SignedPacketSlot';
import CreditAppPanel from './SalesComponents/CreditAppPanel';
import SignatureStatusDialog from './SignatureStatusDialog';
import InPersonSignDialog from './InPersonSignDialog';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useDatePickerSlotProps } from '../../utilities/ModalStackContext';
import { StateManager } from '../../utilities/stateManager';
import RequestManager from '../../utilities/requestManager';
import firebase from '../../utilities/firebase';
import moment from 'moment';
import constants from '../../utilities/constants';
import { useShippingStatus } from '../../components/ShippingStatusChip';

async function getInitialTradeDeduction(tradeIds = []) {
  if (!tradeIds.length) return 0;
  const db = firebase.firestore();
  const docs = await Promise.all(tradeIds.map(id => db.doc(`trades/${id}`).get()));
  return docs.map(d => (d.data() || {}).trade || 0).reduce((a, c) => a + c, 0);
}

export default function Sales(props) {
  const { car = {} } = props;
  const stockNumber = car.stock;
  const deal = car.deal || {};
  const shippingStatus = useShippingStatus(stockNumber, deal);
  const invoices = car.invoices || [];
  const signers = [car.buyer, car.cobuyer].filter(x => !!x);

  const [extraDeposits, setExtraDeposits] = React.useState([]);
  const [deletedDepositIds, setDeletedDepositIds] = React.useState([]);
  const [updatedDeposits, setUpdatedDeposits] = React.useState({});
  const [activeInvoice, setActiveInvoice] = React.useState(
    invoices.find(x => x.id === deal.invoice) || {}
  );
  const [tradeDeduction, setTradeDeduction] = React.useState(0);
  const [salesTeam, setSalesTeam] = React.useState([]);
  const [currentUserFullName, setCurrentUserFullName] = React.useState(StateManager.userName);
  const [sigOpen, setSigOpen] = React.useState(false);
  const [inPersonOpen, setInPersonOpen] = React.useState(false);
  const [inPersonUrl, setInPersonUrl] = React.useState('');
  const [inPersonSigner, setInPersonSigner] = React.useState('');
  const datePickerSlotProps = useDatePickerSlotProps();

  React.useEffect(() => {
    firebase.firestore().collection('users').where('salesRep', '==', true).get().then(snap => {
      const team = snap.docs
        .map(d => ({ id: d.id, name: [d.data().firstName, d.data().lastName].filter(Boolean).join(' ') }))
        .filter(t => t.name)
        .sort((a, b) => a.name.localeCompare(b.name));
      if (team.length) setSalesTeam(team);
    });
    if (StateManager.userID) {
      firebase.firestore().doc(`users/${StateManager.userID}`).get().then(doc => {
        if (doc.exists) {
          const { firstName, lastName } = doc.data();
          const fullName = [firstName, lastName].filter(Boolean).join(' ');
          if (fullName) setCurrentUserFullName(fullName);
        }
      });
    }
  }, []);

  React.useEffect(() => {
    setActiveInvoice(invoices.find(x => x.id === deal.invoice) || {});
  }, [invoices, deal.invoice]);

  React.useEffect(() => {
    getInitialTradeDeduction(deal.trades).then(setTradeDeduction);
  }, []);

  // Permission gating — use sales_rep_id if available, fall back to name for legacy deals
  const isAssignedToCurrentUser = deal.sales_rep_id
    ? deal.sales_rep_id === StateManager.userID
    : deal.sales_rep === currentUserFullName;
  let keysToRemove = [];
  if (!StateManager.isBackoffice() && !!deal.sales_rep && !isAssignedToCurrentUser) {
    keysToRemove = ['sales-rep', 'invoices', 'trades', 'deposits', 'payment', 'expenses', 'profit', 'funding'];
  }
  const canSee = (key) => !keysToRemove.includes(key);

  const disabled = car.deposits >= activeInvoice.total && !StateManager.userName.toLowerCase().includes('max');
  // Non-admins can't backdate the sale date, but an already-saved past date is
  // still valid — clamp minDate to that date so it doesn't show a red error outline.
  const dealDate = deal.date ? moment(deal.date, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD']) : null;
  const saleMinDate = !StateManager.isAdmin()
    ? (dealDate && dealDate.isBefore(moment(), 'day') ? dealDate : moment())
    : undefined;

  // Updaters
  const updateSource = (target, id, value) => {
    const newCar = { ...car };
    newCar[target] = { ...newCar[target], [id]: value };
    car.updater(newCar);
  };

  const updater = (id, value) => {
    firebase.firestore().doc('deals/' + stockNumber).set({ [id]: value }, { merge: true });
    updateSource('deal', id, value);
  };

  const dateUpdate = async (id, date) => {
    const update = {
      [id]: !date ? '' : moment(date).format('MM-DD-YYYY'),
      month: !date ? '' : moment(date).format('YYYY-MM'),
      title_status: !date ? firebase.firestore.FieldValue.delete() : constants.title_statuses[0],
      shipping_status: !date ? firebase.firestore.FieldValue.delete() : constants.shipping_statuses[0],
    };
    await firebase.firestore().doc('deals/' + stockNumber).set(update, { merge: true });
    updateSource('deal', id, date);
  };

  // PDF / signing
  const getJacketPDF = async () => {
    if (!validateForm(car)) return;
    return await RequestManager.get({ function: 'preparePDF', variables: { stockNumber } });
  };

  const viewPDF = async () => {
    StateManager.setLoading(true);
    const pdfDoc = await getJacketPDF();
    if (!pdfDoc?.file) {
      StateManager.setAlertAndOpen('Hmm.. there was an error', 'error');
    } else {
      const a = document.createElement('a');
      a.href = pdfDoc.file;
      a.target = '_blank';
      a.click();
    }
    StateManager.setLoading(false);
  };

  // Map buyer/cobuyer into the { role, name, email } shape the in-house
  // e-signature pipeline expects (createSignatureSession builds the PDF itself).
  const buildSigners = () => {
    const toSigner = (s, role) => s && s.email
      ? { role, email: s.email, name: s.display_name || [s.first_name, s.last_name].filter(Boolean).join(' ').trim() || s.email }
      : null;
    return [toSigner(car.buyer, 'buyer'), toSigner(car.cobuyer, 'cobuyer')].filter(Boolean);
  };

  const sessionVariables = (inPerson) => ({
    docType: 'sales',
    refId: stockNumber,
    doctype: 'in',
    signers: buildSigners(),
    inPerson,
    name: `${car.year} ${car.make} ${car.model} Deal Packet`,
    subject: `${car.year} ${car.make} ${car.model} Deal Packet`,
  });

  // Remote: emails the buyer (then cobuyer) a link to the agora signing page.
  const startSigning = async () => {
    if (!validateForm(car)) return;
    if (!buildSigners().length) return StateManager.setAlertAndOpen('Add a buyer with an email address first.', 'error');
    StateManager.setLoading(true);
    const response = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(false) });
    StateManager.setLoading(false);
    if (response.status === 'complete') StateManager.setAlertAndOpen('Packet sent for signature!', 'success');
    else StateManager.setAlertAndOpen(response.message || 'Hmm... there was an error.', 'error');
  };

  // In-person: creates the session without emailing and opens the signing page
  // on this device / shows a QR for the customer's phone.
  const signInPerson = async () => {
    if (!validateForm(car)) return;
    const list = buildSigners();
    if (!list.length) return StateManager.setAlertAndOpen('Add a buyer first.', 'error');
    StateManager.setLoading(true);
    const response = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(true) });
    StateManager.setLoading(false);
    if (response.status === 'complete') {
      setInPersonUrl(response.signUrl);
      setInPersonSigner(list[0].name);
      setInPersonOpen(true);
    } else StateManager.setAlertAndOpen(response.message || 'Could not start in-person signing.', 'error');
  };

  // Financial computations
  const allDeposits = [...(car.deposits || []), ...extraDeposits]
    .filter(d => !d.id || !deletedDepositIds.includes(d.id))
    .map(d => (d.id && updatedDeposits[d.id]) ? { ...d, ...updatedDeposits[d.id] } : d);
  const totalDeposits = allDeposits.reduce((s, d) => s + (d.amount || 0), 0);
  const totalExpenses = (car.expenses || []).reduce((s, e) => s + (e.amount || 0), 0);
  const amountOwed = (activeInvoice?.total || 0) - totalDeposits;
  const profit = (activeInvoice?.revenue || 0) - totalExpenses;
  const showFunding = canSee('funding') && !!(activeInvoice?.total);
  const showProfit = canSee('profit') && !!(activeInvoice?.revenue);

  // Completion chips. The "Signed" chip is clickable and opens the signature
  // status dialog (sent/opened/signed per signer + cancel + view).
  const chips = [
    { label: 'Loaded', done: !!(deal.date && car.buyer?.display_name && deal.invoice) },
    { label: 'Signed', done: !!(car.sales_doc), onClick: () => setSigOpen(true) },
    { label: 'Paid', done: !!(activeInvoice?.total && totalDeposits >= activeInvoice.total) },
    { label: 'Shipped', done: shippingStatus === 'delivered' },
  ];

  return (
    <Box className="no-print" sx={{ mx: -2, mt: -2 }}>

      {/* Sticky action bar */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider',
        px: { xs: 2, sm: 3 }, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {chips.map(({ label, done, onClick }) => (
            <Chip
              key={label}
              label={label}
              size="small"
              icon={done
                ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />
              }
              color={done ? 'success' : 'default'}
              variant={done ? 'filled' : 'outlined'}
              onClick={onClick}
              clickable={!!onClick}
              data-sold-ok={label === 'Signed' || undefined}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={viewPDF} data-sold-ok>Preview Packet</Button>
          <Button variant="outlined" size="small" onClick={signInPerson}>Sign in Person</Button>
          <Button variant="contained" size="small" onClick={startSigning}>Send for Signature</Button>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Grid container spacing={2}>

          {/* Left column — Parties */}
          <Grid item xs={12} md={6}>

            {/* Sale Info */}
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <SectionHeader>Sale Info</SectionHeader>
              <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Sale Date"
                    value={dealDate}
                    onChange={(newValue) => dateUpdate('date', newValue ? newValue.format('YYYY-MM-DD') : null)}
                    minDate={saleMinDate}
                    disabled={disabled}
                    slotProps={{
                      field: { clearable: true },
                      textField: { size: 'small', fullWidth: true },
                      popper: datePickerSlotProps.popper,
                      dialog: datePickerSlotProps.dialog,
                    }}
                  />
                </LocalizationProvider>
                <FormControl fullWidth size="small" disabled={!!deal.date && !!deal.sales_rep && !StateManager.isManager() && !isAssignedToCurrentUser}>
                  <InputLabel>Sales Rep</InputLabel>
                  <Select
                    value={deal.sales_rep_id || salesTeam.find(t => t.name === deal.sales_rep)?.id || ''}
                    label="Sales Rep"
                    onChange={(e) => {
                      const selected = salesTeam.find(t => t.id === e.target.value);
                      if (!selected) return;
                      firebase.firestore().doc('deals/' + stockNumber).set({
                        sales_rep: selected.name,
                        sales_rep_id: selected.id,
                      }, { merge: true });
                      updateSource('deal', 'sales_rep', selected.name);
                      updateSource('deal', 'sales_rep_id', selected.id);
                    }}
                  >
                    {salesTeam.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            {/* Buyer */}
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <SectionHeader>Buyer</SectionHeader>
              <CustomerCard customer={car.buyer} stockNumber={stockNumber} type="buyer" disabled={disabled} />
              <LicenseUpload customer={car.buyer} showBack disabled={disabled} />
            </Paper>

            {/* Co-Buyer */}
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <SectionHeader>Co-Buyer</SectionHeader>
              <CustomerCard customer={car.cobuyer} stockNumber={stockNumber} type="cobuyer" disabled={disabled} />
              <LicenseUpload customer={car.cobuyer} showBack={false} disabled={disabled} />
            </Paper>

            {/* Credit Application */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <SectionHeader>Credit Application</SectionHeader>
              <CreditAppPanel car={car} />
            </Paper>
          </Grid>

          {/* Right column — Deal */}
          <Grid item xs={12} md={6}>

            {/* Invoice */}
            {canSee('invoices') && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <SectionHeader>Invoice</SectionHeader>
                <InvoicePanel
                  invoices={invoices}
                  deal={deal}
                  stockNumber={stockNumber}
                  disabled={disabled}
                  tradeDeduction={tradeDeduction}
                  onInvoiceSelectionChange={(inv) => setActiveInvoice(inv || {})}
                />
              </Paper>
            )}

            {/* Trades */}
            {canSee('trades') && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <SectionHeader>Trades</SectionHeader>
                <TradesPanel
                  deal={{ ...deal, car: stockNumber }}
                  disabled={disabled}
                  onTradesChange={setTradeDeduction}
                  onDepositCreated={(deposit) => setExtraDeposits(prev => [...prev, deposit])}
                />
              </Paper>
            )}

            {/* Deposits */}
            {canSee('deposits') && (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <SectionHeader>Deposits & Payment</SectionHeader>
                <DepositsPanel
                  deposits={allDeposits}
                  stockNumber={stockNumber}
                  invoiceTotal={activeInvoice?.total || 0}
                  disabled={disabled}
                  car={car}
                  onDepositAdded={(d) => setExtraDeposits(prev => [...prev, d])}
                  onDepositDeleted={(id) => setDeletedDepositIds(prev => [...prev, id])}
                  onDepositUpdated={(d) => setUpdatedDeposits(prev => ({ ...prev, [d.id]: d }))}
                />
              </Paper>
            )}
          </Grid>

          {/* Financial summary tiles */}
          {(showFunding || showProfit) && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {showFunding && (
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography variant="overline" sx={{ fontSize: 11, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Amount Owed
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: amountOwed < 0 ? 'error.main' : 'text.primary' }}>
                        {amountOwed.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {showProfit && (
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography variant="overline" sx={{ fontSize: 11, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Deal Profit
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: profit < 0 ? 'error.main' : 'success.main' }}>
                        {profit.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}

          {/* Expenses — full width */}
          {canSee('expenses') && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <SectionHeader>Expenses</SectionHeader>
                <ExpensesPanel expenses={car.expenses || []} stockNumber={stockNumber} disabled={disabled} />
              </Paper>
            </Grid>
          )}

          {/* Documents */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <SectionHeader>Documents</SectionHeader>
              <SignedPacketSlot car={car} disabled={disabled} />
            </Paper>
          </Grid>

        </Grid>
      </Box>

      <SignatureStatusDialog
        open={sigOpen}
        onClose={() => setSigOpen(false)}
        car={car}
        type="sales"
        label="Sales Packet"
      />

      <InPersonSignDialog
        open={inPersonOpen}
        onClose={() => setInPersonOpen(false)}
        signUrl={inPersonUrl}
        signerName={inPersonSigner}
      />
    </Box>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        px: 2, pt: 1.5, pb: 0.5,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
        color: 'text.secondary',
        borderBottom: 1, borderColor: 'divider',
      }}
    >
      {children}
    </Typography>
  );
}

function validateForm(data) {
  let message = false;
  if (!data.deal.date) message = 'Missing Date';
  else if (!data.buyer) message = 'Missing Buyer Data';
  else if (!data.buyer?.email) message = 'Missing Buyer Email';
  else if (!data.buyer?.license) message = 'Missing Buyer Driver License';
  else if (!data.buyer?.license_back) message = 'Missing Back of Buyer Driver License';
  else if (!data.deal.invoice) message = 'Missing Invoice Data';
  else if (!data.year) message = 'Missing Car Year';
  else if (!data.make) message = 'Missing Car Make';
  else if (!data.model) message = 'Missing Car Model';
  else if (!data.vin) message = 'Missing VIN';
  else if (!data.miles) message = 'Missing Miles';
  else if (!data.color) message = 'Missing Color';

  if (data.cobuyer && Object.keys(data.cobuyer).length > 0) {
    if (!data.cobuyer?.email) message = 'Missing Cobuyer Email';
    else if (!data.cobuyer?.license) message = 'Missing Cobuyer Driver License';
  }
  if (!message) return 'success';
  StateManager.setLoading(false);
  StateManager.setAlertAndOpen(message, 'error');
}
